import { useListACHTransactionsQuery } from 'api/giftcard-ach-service/giftcard-ach-service';
import { getDateRangeOptions } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import { useStoreIdSearchParam } from 'components/search/search-params.hooks';
import { useSearch } from 'components/search/search.hooks';
import {
  defaultSearchParams,
  luxonDateSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

export const useACHTransactionsSearchParams = () => {
  const { t } = useTranslation();
  const statusOptions = [
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.all',
      ),
      id: 'ALL',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.cancelled',
      ),
      id: 'CANCELLED',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.failed',
      ),
      id: 'FAILED',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.pending',
      ),
      id: 'PENDING',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.processed',
      ),
      id: 'PROCESSED',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.retryInitiated',
      ),
      id: 'RETRY_INITIATED',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.skipped',
      ),
      id: 'SKIPPED',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.submittedToDwolla',
      ),
      id: 'SUBMITTED_TO_DWOLLA',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.submissionToDwollaFailed',
      ),
      id: 'SUBMITTION_TO_DWOLLA_FAILED',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.submissionToDwollaPending',
      ),
      id: 'SUBMITTION_TO_DWOLLA_PENDING',
    },
    {
      label: t(
        'page.giftCards.moneyMovement.sections.transactions.filters.statuses.validationError',
      ),
      id: 'VALIDATION_ERROR',
    },
  ];

  const searchParams = [
    ...defaultSearchParams(),
    luxonDateSearchParam('startDate', {
      defaultValue: DateTime.local().minus({ days: 30 }).startOf('day'),
      filter: {
        type: 'DateRangeFilter',
        label: t('forms.fields.dateRange.label'),
        showName: true,
        startParamName: 'startDate',
        endParamName: 'endDate',
        predefinedDateOptions: getDateRangeOptions([
          'lpLast7Days',
          'lpLast30Days',
          'lpLast60Days',
          'lpLast90Days',
          'lpLast365Days',
          'last-month',
          'last-quarter',
          'last-year',
          'custom',
        ]),
      },
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
    luxonDateSearchParam('endDate', {
      defaultValue: DateTime.local().endOf('day'),
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
    stringSearchParam('status', {
      defaultValue: 'ALL',
      filter: {
        type: 'SingleSelectFilter',
        label: t('forms.fields.status.label'),
      },
      options: statusOptions,
    }),
    ...useStoreIdSearchParam(),
  ];

  return useSearch(useListACHTransactionsQuery, searchParams, {});
};
